/** Date helpers shared across repositories and views. */

/** Epoch values below this are implausible as milliseconds (≈ 1973), so treat them as seconds. */
const SECONDS_CUTOFF = 100_000_000_000

/**
 * Normalise every date shape this database actually contains to an ISO string.
 *
 * The Skida collections mix all of these — a single `users` document can carry
 * `createdAt` as an epoch number, `mailchimpBackfillAt` as a Firestore
 * Timestamp and `promoAccess.redeemedAt` as an ISO string. CRM-owned documents
 * use serverTimestamp(), so they arrive as Timestamps.
 */
export function toIsoOrNull(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null

  // Epoch milliseconds (or seconds) — how consumer `users.createdAt` is stored.
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return null
    const ms = value < SECONDS_CUTOFF ? value * 1000 : value
    const d = new Date(ms)
    return Number.isNaN(d.getTime()) ? null : d.toISOString()
  }

  if (typeof value === 'string') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d.toISOString()
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString()
  }

  // Firestore Timestamp (client SDK exposes toDate()).
  if (typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate: () => Date }).toDate().toISOString()
    } catch {
      return null
    }
  }

  // Raw Timestamp shape, as returned by the REST API or a plain JSON import.
  if (typeof value === 'object' && 'seconds' in value) {
    const secs = (value as { seconds: unknown }).seconds
    if (typeof secs === 'number') return new Date(secs * 1000).toISOString()
  }

  return null
}

/**
 * Dates are Norwegian, text is en-US — decided 12 Aug 2026.
 *
 * Those two rules meet in the month name. `{ month: 'short' }` under `nb-NO`
 * gives `aug.`; under `en-US` it gives `Aug`. A month rendered as a word is
 * text, so it follows the text rule — a lowercase `aug.` sitting under an
 * English column heading reads as a bug rather than as a locale.
 *
 * Purely numeric dates and every number go the other way, through `nf` and
 * `nb-NO`: `12.08.2026`, `44 232`. Raised with the designer, whose prototype
 * uses four different formatters for this.
 */
const DISPLAY = new Intl.DateTimeFormat('en-US', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

/** "03 Feb 2026" — the format used throughout the design. Em-dash when absent. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '–'
  return DISPLAY.format(d)
}

/** Year only, for the "Skida user since 2020" badge. */
export function formatYear(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : String(d.getFullYear())
}

/**
 * Today, as a `YYYY-MM-DD` string — the one clock this app reads.
 *
 * ## Why it is local, and why there is only one of it
 *
 * There were seven independent versions of this, each written
 * `new Date().toISOString().slice(0, 10)`, which is **UTC**. Norway runs two
 * hours ahead of it in summer, so between midnight and 02:00 local the app
 * believed it was still yesterday — while anything that built a date from local
 * parts, as the calendar grid does, believed it was today. Two clocks in one
 * screen, and the designer's 15 Aug round caught what that produces: a card
 * marked "Today" sitting under the heading PAST.
 *
 * Local wins because every date in this system is a human's date. A task is due
 * on the day the person owning it calls that day; a post goes out on the date
 * printed in the plan. Neither has anything to do with Greenwich.
 *
 * Anything comparing a stored `YYYY-MM-DD` against "now" reads this, so a
 * comparison and a label can never disagree about which day it is.
 */
export function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** The same, `days` from now. Local, for the same reason. */
export function isoInDays(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}


/**
 * `days` after an ISO date, still ISO, still local.
 *
 * The payment deadline was a day short for a month, in both browser runs, and
 * this is why: the old version added the days to a local date and then read the
 * answer out of `toISOString()`. That converts to UTC, and Norway is an hour or
 * two ahead — so local midnight on the 31st is 22:00 on the 30th in UTC, and
 * the date somebody is asked to pay by came out a day early.
 *
 * Arithmetic on a `Date` is fine; reading it back through UTC is not. The same
 * mistake as the two clocks in the calendar grid, and the reason `todayIso`
 * above builds its string from local components too.
 */
export function plusDaysIso(iso: string, days: number): string {
  const d = iso ? new Date(`${iso}T00:00:00`) : new Date()
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * `15.08.2026` — the date as a *document* writes it, always absolute.
 *
 * Decision 53, and it comes from a defect worth remembering: a board minute
 * titled "Styremøte — Today", with every signature line reading "Lillesand den:
 * Today". The meeting date defaults to today, so that was the common case rather
 * than an edge one.
 *
 * The rule for choosing between this and the screen's formatters is one line:
 * **relative for the screen, absolute for the document.** Anything a person
 * signs, files, or is asked to pay against is read on a different day than it
 * was made, so a word like "Today" in it is meaningless at best and wrong in
 * anything legal. On a screen the same word is the most useful reading there is.
 */
export function fmtDateAbs(iso: string): string {
  const [y, m, d] = (iso || '').split('-')
  return y && m && d ? `${d}.${m}.${y}` : '—'
}
